/** Seed data for the Customer Management workspace (Demo Mode only, local-only - no backend
 * persistence yet). Backs the customer-first Customer Hub (dashboard/list, all customers,
 * onboarding pipeline, contract renewals) and the per-customer workspace (Overview, Commercials,
 * Contracts, Sites, Contacts, Mobilisation, Reporting, Activity tabs). Covers the "win or renew
 * the client contract" journey milestone - turning a signed contract into a controlled operating
 * model before any ordering or range planning activity begins. All figures are static mock data,
 * matching the "seeded illustrative numbers" approach used by `src/pages/EstateManagement` and
 * `supplierProfileData.ts`. Keyed by customerId (slug), falling back to a generated default for
 * any customer not explicitly seeded below - same precedent as `supplierProfileData.ts`. */

export type OnboardingStatus = 'Complete' | 'InProgress' | 'Blocked' | 'NotStarted';
export type ReadinessStatus = 'Complete' | 'InReview' | 'Partial' | 'Missing' | 'NotStarted' | 'Blocked';
export type RiskLevel = 'Low' | 'Medium' | 'High';

export const ONBOARDING_STATUS_LABEL: Record<OnboardingStatus, string> = {
  Complete: 'Complete',
  InProgress: 'In Progress',
  Blocked: 'Blocked',
  NotStarted: 'Not Started',
};

export const READINESS_STATUS_LABEL: Record<ReadinessStatus, string> = {
  Complete: 'Complete',
  InReview: 'In Review',
  Partial: 'Partial',
  Missing: 'Missing',
  NotStarted: 'Not Started',
  Blocked: 'Blocked',
};

export interface CustomerSectionStatus {
  status: ReadinessStatus;
  /** Optional supporting detail, e.g. "Missing finance contact" */
  detail?: string;
}

export interface CustomerSectionStatuses {
  customerProfile: CustomerSectionStatus;
  commercialModel: CustomerSectionStatus;
  contractsTerms: CustomerSectionStatus;
  sites: CustomerSectionStatus;
  contacts: CustomerSectionStatus;
  reportingRequirements: CustomerSectionStatus;
  mobilisationPlan: CustomerSectionStatus;
}

export interface CustomerContract {
  contractRef: string;
  startDate: string;
  endDate: string;
  noticePeriod: string;
  renewalTimer: string;
  status: 'Active' | 'Renewal Due';
  slas: string[];
  obligations: string[];
  milestones: { milestone: string; date: string }[];
}

export interface CustomerCommercial {
  model: string;
  contractValue: string;
  pricingBasis: string;
  managementFee: string;
  rebate: string;
  billingRules: string;
  paymentTerms: string;
  margin: string;
  approvalStatus: 'Approved' | 'PendingApproval' | 'InReview';
  assumptions: string[];
}

export interface CustomerSite {
  site: string;
  region: string;
  serviceModel: string;
  goLiveDate: string;
  status: 'Live' | 'Mobilising' | 'Planned';
  headcount: number;
  requirements: string;
}

export interface CustomerContact {
  name: string;
  role: string;
  category: 'Client' | 'Internal Owner' | 'Escalation' | 'Finance' | 'Operational' | 'Reporting' | 'Decision Maker';
  site: string;
  email: string;
  phone: string;
  /** Conceptual/demo-only HubSpot link for this contact - see the "CRM integration (Demo Mode
   * only)" block below for helpers. Absent/undefined = not linked to HubSpot. */
  crm?: ContactCrmDetails;
}

/* ---------------------------------------------------------------------------------------------
 * Conceptual HubSpot CRM integration (Demo Mode only, frontend/mock only - no real HubSpot API
 * connection, authentication or background sync). Deliberately lightweight: a compact status
 * summary + a couple of editable fields per client, plus an optional linked-contact id per
 * contact. Not a generic integration framework - if a second CRM provider is ever needed, this
 * would need a proper redesign rather than extending `provider` into a big union.
 * ------------------------------------------------------------------------------------------- */

export type CrmProvider = 'HubSpot';

/** Compact status shown as a badge on the client's HubSpot summary card. */
export type CustomerCrmStatus = 'linked' | 'not-linked' | 'sync-overdue' | 'attention-required';

export interface CustomerCrmDetails {
  provider: CrmProvider;
  /** Mock HubSpot portal id - shared/configurable, not per-client in real HubSpot but kept here
   * so each client's URLs can be built without a separate global-config lookup. */
  portalId: string;
  companyId: string | null;
  companyUrl: string | null;
  status: CustomerCrmStatus;
  syncEnabled: boolean;
  lastSyncAt: string | null;
  /** Mock-only supporting detail for the `attention-required` state - never shown directly on
   * the summary card, only in a tooltip/expandable message (see `HubSpotSummaryCard`). */
  lastSyncError?: string;
}

export interface ContactCrmDetails {
  provider: CrmProvider;
  contactId: string | null;
  contactUrl: string | null;
}

/** Configurable mock HubSpot portal id, used whenever a client has no `crm.portalId` yet. */
export const HUBSPOT_MOCK_PORTAL_ID = '4872156';

export function buildHubSpotCompanyUrl(portalId: string, companyId: string): string {
  return `https://app.hubspot.com/contacts/${portalId}/company/${companyId}`;
}

export function buildHubSpotContactUrl(portalId: string, contactId: string): string {
  return `https://app.hubspot.com/contacts/${portalId}/contact/${contactId}`;
}

const DEFAULT_CUSTOMER_CRM: CustomerCrmDetails = {
  provider: 'HubSpot',
  portalId: HUBSPOT_MOCK_PORTAL_ID,
  companyId: null,
  companyUrl: null,
  status: 'not-linked',
  syncEnabled: true,
  lastSyncAt: null,
};

/** Returns the client's own CRM details if seeded, otherwise a "not linked" default - same
 * fallback pattern as `getCustomerDocuments`/`getServiceRequirements`. */
export function getClientCrmDetails(customer: CustomerRecord): CustomerCrmDetails {
  return customer.crm ?? DEFAULT_CUSTOMER_CRM;
}

/** Formats a mock sync timestamp for display, e.g. "Today, 09:42" or "12/07/2026, 14:03". */
export function formatHubSpotLastSynced(lastSyncAt: string | null): string {
  if (!lastSyncAt) return 'Not yet synchronised';
  const date = new Date(lastSyncAt);
  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const isToday = date.toDateString() === new Date().toDateString();
  return isToday ? `Today, ${time}` : `${date.toLocaleDateString()}, ${time}`;
}

/** Builds an optional `ContactCrmDetails` from a raw HubSpot contact id input (e.g. a contact
 * form field) - trims whitespace, returns `undefined` when cleared/empty so the contact simply
 * has no CRM link (never deletes/changes any other contact data). */
export function buildContactCrmDetails(rawContactId: string, portalId: string): ContactCrmDetails | undefined {
  const trimmed = rawContactId.trim();
  if (!trimmed) return undefined;
  return { provider: 'HubSpot', contactId: trimmed, contactUrl: buildHubSpotContactUrl(portalId, trimmed) };
}

/** lazy: in-memory only - links a client to a HubSpot company record. Upgrade path: replace with
 * a real HubSpot API call once a backend integration exists. */
export function linkClientToHubSpot(customerId: string, rawCompanyId: string): void {
  const customer = CUSTOMERS.find((c) => c.id === customerId);
  const trimmed = rawCompanyId.trim();
  if (!customer || !trimmed) return;
  const portalId = customer.crm?.portalId ?? HUBSPOT_MOCK_PORTAL_ID;
  customer.crm = {
    provider: 'HubSpot',
    portalId,
    companyId: trimmed,
    companyUrl: buildHubSpotCompanyUrl(portalId, trimmed),
    status: 'linked',
    syncEnabled: customer.crm?.syncEnabled ?? true,
    lastSyncAt: new Date().toISOString(),
  };
}

/** lazy: in-memory only - clears the company link but leaves every other client/contact field
 * untouched (per the "this will not delete the client or its HubSpot record" confirmation). */
export function unlinkClientFromHubSpot(customerId: string): void {
  const customer = CUSTOMERS.find((c) => c.id === customerId);
  if (!customer?.crm) return;
  customer.crm = {
    ...customer.crm, companyId: null, companyUrl: null, status: 'not-linked', lastSyncError: undefined,
  };
}

/** lazy: in-memory only - mock sync, just refreshes the timestamp and clears any error/warning
 * status. Upgrade path: replace with a real HubSpot sync call once a backend integration exists. */
export function syncClientWithHubSpot(customerId: string): void {
  const customer = CUSTOMERS.find((c) => c.id === customerId);
  if (!customer?.crm?.companyId) return;
  customer.crm = {
    ...customer.crm, status: 'linked', lastSyncAt: new Date().toISOString(), lastSyncError: undefined,
  };
}

export function setClientHubSpotSyncEnabled(customerId: string, enabled: boolean): void {
  const customer = CUSTOMERS.find((c) => c.id === customerId);
  if (!customer) return;
  customer.crm = { ...(customer.crm ?? DEFAULT_CUSTOMER_CRM), syncEnabled: enabled };
}

export const CRM_STATUS_LABEL: Record<CustomerCrmStatus, string> = {
  linked: 'Linked',
  'not-linked': 'Not linked',
  'sync-overdue': 'Sync overdue',
  'attention-required': 'Attention required',
};

export const CRM_STATUS_COLOR: Record<CustomerCrmStatus, 'success' | 'default' | 'warning' | 'error'> = {
  linked: 'success',
  'not-linked': 'default',
  'sync-overdue': 'warning',
  'attention-required': 'error',
};

export interface CustomerMobilisationTask {
  task: string;
  owner: string;
  dueDate: string;
  status: 'Complete' | 'In Progress' | 'Not Started' | 'Blocked';
  dependency?: string;
}

export interface CustomerReportingRequirement {
  reportType: string;
  frequency: string;
  format: string;
  nextDue: string;
}

export interface CustomerActivityEntry {
  date: string;
  actor: string;
  action: string;
}

/** A contract, legal or due-diligence document held against a customer. Modelled on the OFD
 * retailer onboarding wizard's document structure (`RetailerDocumentModel` in `ofdClient.ts`,
 * used by `pages/orderly-for-distributors/Onboarding/components/OnboardingDueDiligence.tsx`) so
 * Customer Management can reuse the same list/upload/download/approve pattern via
 * `pages/CustomerManagement/components/DocumentChecklist.tsx` - but kept local/mocked since
 * there is no Customer Management document backend yet. */
export interface CustomerDocument {
  documentId: number;
  /** Groups documents into sections, e.g. "Contracts & Legal Agreements", "Due Diligence". */
  header: string;
  category: string;
  fileExists: boolean;
  fileName?: string;
  approved: boolean;
  /** Whether this document still needs a file uploaded (vs. a simple attestation checkbox). */
  uploadRequired: boolean;
}

/** Fallback document checklist used whenever a customer has no `documents` seeded explicitly. */
export const DEFAULT_CUSTOMER_DOCUMENTS: CustomerDocument[] = [
  { documentId: 1, header: 'Contracts & Legal Agreements', category: 'Signed Master Services Agreement', fileExists: true, fileName: 'MSA-signed.pdf', approved: true, uploadRequired: true },
  { documentId: 2, header: 'Contracts & Legal Agreements', category: 'Data Processing Agreement', fileExists: true, fileName: 'DPA-signed.pdf', approved: true, uploadRequired: true },
  { documentId: 3, header: 'Contracts & Legal Agreements', category: 'Certificate of Insurance', fileExists: false, approved: false, uploadRequired: true },
  { documentId: 4, header: 'Contracts & Legal Agreements', category: 'Site-Specific Schedule', fileExists: false, approved: false, uploadRequired: true },
  { documentId: 5, header: 'Due Diligence', category: 'Food Safety Policy', fileExists: true, fileName: 'food-safety-policy.pdf', approved: true, uploadRequired: true },
  { documentId: 6, header: 'Due Diligence', category: 'Allergen Management Policy', fileExists: false, approved: false, uploadRequired: true },
  { documentId: 7, header: 'Due Diligence', category: 'Financial Reference', fileExists: false, approved: false, uploadRequired: true },
];

/** Returns the customer's own document checklist if seeded, otherwise the shared default. */
export function getCustomerDocuments(customer: CustomerRecord): CustomerDocument[] {
  return customer.documents ?? DEFAULT_CUSTOMER_DOCUMENTS;
}

/** Captured during the new-customer onboarding intake wizard (Customer Management, Demo Mode
 * only - see `pages/CustomerManagement/OnboardingIntake`). Describes the catering/service
 * requirements a customer brings into the relationship, distinct from the ongoing
 * `CustomerCommercial`/`CustomerReportingRequirement` records used once the account is live. */
export interface CustomerServiceRequirements {
  userTypes: string[];
  serviceTimes: string[];
  menus: string[];
  dietaryRequirements: string[];
  allergens: string[];
  hospitalityNeeds: string;
  budget: string;
  pricingModel: string;
  complianceExpectations: string[];
  reportingRequirements: string[];
}

/** Fallback used whenever a customer has no `serviceRequirements` seeded explicitly (only
 * customers created via the onboarding intake wizard have this populated at creation time). */
export const EMPTY_SERVICE_REQUIREMENTS: CustomerServiceRequirements = {
  userTypes: [],
  serviceTimes: [],
  menus: [],
  dietaryRequirements: [],
  allergens: [],
  hospitalityNeeds: '',
  budget: '',
  pricingModel: '',
  complianceExpectations: [],
  reportingRequirements: [],
};

/** Returns the customer's own service requirements if seeded, otherwise the shared empty
 * default - used by the onboarding detail page's Requirements tab so the fields can be captured
 * for ANY onboarding customer, not just ones created via the intake wizard. */
export function getServiceRequirements(customer: CustomerRecord): CustomerServiceRequirements {
  return customer.serviceRequirements ?? EMPTY_SERVICE_REQUIREMENTS;
}

export interface CustomerRecord {
  id: string;
  name: string;
  sector: string;
  operatingModel: string;
  owner: string;
  sitesCount: number;
  status: 'Active' | 'Onboarding' | 'Renewal Due';
  onboardingStatus: OnboardingStatus;
  commercialReadiness: ReadinessStatus;
  riskLevel: RiskLevel;
  blockers: string[];
  missingInfo: string[];
  nextBestAction: string;
  renewalDate: string;
  targetGoLive: string;
  sectionStatuses: CustomerSectionStatuses;
  contract: CustomerContract;
  commercial: CustomerCommercial;
  sites: CustomerSite[];
  contacts: CustomerContact[];
  mobilisation: CustomerMobilisationTask[];
  reporting: CustomerReportingRequirement[];
  activity: CustomerActivityEntry[];
  /** Only populated for customers created via the onboarding intake wizard. */
  serviceRequirements?: CustomerServiceRequirements;
  /** Contract/legal/due-diligence documents - falls back to `DEFAULT_CUSTOMER_DOCUMENTS` via
   * `getCustomerDocuments()` when not explicitly seeded. */
  documents?: CustomerDocument[];
  /** Launch sign-off dates, captured on the onboarding detail page's Launch Sign-off tab. */
  internalSignOffDate?: string;
  customerSignOffDate?: string;
  hypercareStartDate?: string;
  /** Formal journey stage - see `JOURNEY_STAGE_LABEL` below. Drives which of the two customer
   * detail routes a customer belongs on: `Prospect`-`GoLiveReady` -> the onboarding detail page
   * (`/customer-management/onboarding/:customerId`), `Live`/`Optimising` -> the operational
   * customer workspace (`/customer-management/customers/:customerId`). */
  journeyStage: JourneyStage;
  /** Go-live readiness checklist, grouped by the milestone categories required to launch a
   * customer (commercial setup, site setup, supplier & procurement setup, product & menu
   * readiness, operational readiness, data & governance checks, launch sign-off). Drives the
   * progress tiles on the onboarding detail page. */
  onboardingMilestones: OnboardingMilestoneCategory[];
  /** Conceptual/demo-only HubSpot link for this client - falls back to a "not linked" default
   * via `getClientCrmDetails()` when not explicitly seeded. */
  crm?: CustomerCrmDetails;
}

/** Formal onboarding journey stage, narrated in the demo as:
 * Prospect / Contract won -> Mobilising -> Onboarding -> Go-live ready -> Live customer -> Optimising */
export type JourneyStage = 'Prospect' | 'ContractWon' | 'Mobilising' | 'Onboarding' | 'GoLiveReady' | 'Live' | 'Optimising';

export const JOURNEY_STAGE_ORDER: JourneyStage[] = ['Prospect', 'ContractWon', 'Mobilising', 'Onboarding', 'GoLiveReady', 'Live', 'Optimising'];

export const JOURNEY_STAGE_LABEL: Record<JourneyStage, string> = {
  Prospect: 'Prospect',
  ContractWon: 'Contract Won',
  Mobilising: 'Mobilising',
  Onboarding: 'Onboarding',
  GoLiveReady: 'Go-Live Ready',
  Live: 'Live Customer',
  Optimising: 'Optimising',
};

export const JOURNEY_STAGE_COLOR: Record<JourneyStage, 'default' | 'primary' | 'secondary' | 'warning' | 'success' | 'info'> = {
  Prospect: 'default',
  ContractWon: 'info',
  Mobilising: 'secondary',
  Onboarding: 'warning',
  GoLiveReady: 'primary',
  Live: 'success',
  Optimising: 'success',
};

/** Journey stages considered "still onboarding" - shown on the Onboarding Pipeline list and
 * routed to the onboarding detail page. Everything else (`Live`/`Optimising`) is an operational
 * customer, shown on the Customers list and routed to the operational workspace. */
export const ONBOARDING_JOURNEY_STAGES: JourneyStage[] = ['Prospect', 'ContractWon', 'Mobilising', 'Onboarding', 'GoLiveReady'];

export function isOnboardingCustomer(customer: CustomerRecord): boolean {
  return ONBOARDING_JOURNEY_STAGES.includes(customer.journeyStage);
}

/** Go-live readiness milestone categories, per the required customer launch checklist. Each
 * category groups the individual checklist items needed to transition a customer to `Live`. */
export type OnboardingMilestoneCategoryKey =
  | 'serviceRequirements'
  | 'commercialSetup'
  | 'siteSetup'
  | 'supplierProcurementSetup'
  | 'productMenuReadiness'
  | 'operationalReadiness'
  | 'dataGovernanceChecks'
  | 'launchSignOff';

export interface OnboardingMilestoneItem {
  key: string;
  label: string;
  complete: boolean;
}

export interface OnboardingMilestoneCategory {
  key: OnboardingMilestoneCategoryKey;
  label: string;
  items: OnboardingMilestoneItem[];
}

const ONBOARDING_MILESTONE_TEMPLATE: { key: OnboardingMilestoneCategoryKey; label: string; itemLabels: string[] }[] = [
  {
    key: 'serviceRequirements',
    label: 'Service requirements',
    itemLabels: [
      'Type of users defined',
      'Service times captured',
      'Menus captured',
      'Dietary requirements captured',
      'Allergens captured',
      'Hospitality needs captured',
      'Budget captured',
      'Pricing model captured',
      'Compliance expectations captured',
      'Reporting requirements captured',
    ],
  },
  {
    key: 'commercialSetup',
    label: 'Commercial',
    itemLabels: [
      'Client/customer record created',
      'Contract captured',
      'Contract dates, renewal dates and notice periods recorded',
      'Site group or operating model defined',
      'Reporting obligations captured',
    ],
  },
  {
    key: 'siteSetup',
    label: 'Site',
    itemLabels: [
      'Sites/locations created',
      'Site contacts and ownership assigned',
      'Trading calendars, opening days and delivery windows configured',
      'Site-specific requirements captured',
    ],
  },
  {
    key: 'supplierProcurementSetup',
    label: 'Supplier and procurement',
    itemLabels: [
      'Approved suppliers assigned to the relevant sites',
      'Supplier status approved',
      'Delivery rules configured',
      'Price lists loaded',
      'Catalogue items approved',
      'Site-specific availability configured',
      'Blocked/off-contract items governed',
    ],
  },
  {
    key: 'productMenuReadiness',
    label: 'Product and menu',
    itemLabels: [
      'Ingredients/products created or imported',
      'Allergens and dietary data completed',
      'Pack sizes and units of measure checked',
      'Sell prices and cost prices confirmed',
      'Menu/product trial plan agreed where relevant',
    ],
  },
  {
    key: 'operationalReadiness',
    label: 'Operational',
    itemLabels: [
      'Users invited',
      'Roles and permissions assigned',
      'Training completed or scheduled',
      'Support route agreed',
      'Initial order, stock, recipe or menu workflows tested',
      'Known gaps accepted or logged',
    ],
  },
  {
    key: 'dataGovernanceChecks',
    label: 'Data and governance checks',
    itemLabels: [
      'Missing supplier/product/allergen data reviewed',
      'Approval statuses confirmed',
      'Price changes and start/end dates validated',
      'Reporting dashboard shows enough live-ready data',
    ],
  },
  {
    key: 'launchSignOff',
    label: 'Launch sign-off',
    itemLabels: [
      'Internal owner signs off',
      'Customer owner signs off',
      'Go-live date confirmed',
      'Hypercare period starts',
    ],
  },
];

/** Builds the full 7-category milestone checklist for a customer. `overrides` supplies an
 * explicit `complete` flag per item (by index) for any category that isn't uniformly
 * complete/incomplete; any category not present in `overrides` falls back to `defaultComplete`
 * for every item (`true` for Live/Optimising customers, `false` for early-stage prospects). */
export function buildMilestoneCategories(overrides: Partial<Record<OnboardingMilestoneCategoryKey, boolean[]>>, defaultComplete: boolean): OnboardingMilestoneCategory[] {
  return ONBOARDING_MILESTONE_TEMPLATE.map((category) => {
    const overrideFlags = overrides[category.key];
    return {
      key: category.key,
      label: category.label,
      items: category.itemLabels.map((label, index) => ({
        key: `${category.key}-${index}`,
        label,
        complete: overrideFlags ? !!overrideFlags[index] : defaultComplete,
      })),
    };
  });
}

export interface MilestoneCategoryProgress {
  key: OnboardingMilestoneCategoryKey;
  label: string;
  completedCount: number;
  totalCount: number;
  percent: number;
  incompleteItems: string[];
}

export interface MilestoneProgress {
  overallPercent: number;
  completedCategories: number;
  totalCategories: number;
  totalIncompleteItems: number;
  categories: MilestoneCategoryProgress[];
}

/** Computes go-live readiness progress from a customer's milestone checklist - drives the
 * progress-focused dashboard tiles on the onboarding detail page. */
export function getMilestoneProgress(customer: CustomerRecord): MilestoneProgress {
  const categories: MilestoneCategoryProgress[] = customer.onboardingMilestones.map((category) => {
    const completedCount = category.items.filter((item) => item.complete).length;
    const totalCount = category.items.length;
    return {
      key: category.key,
      label: category.label,
      completedCount,
      totalCount,
      percent: totalCount === 0 ? 100 : Math.round((completedCount / totalCount) * 100),
      incompleteItems: category.items.filter((item) => !item.complete).map((item) => item.label),
    };
  });
  const totalCount = categories.reduce((total, category) => total + category.totalCount, 0);
  const completedCount = categories.reduce((total, category) => total + category.completedCount, 0);
  return {
    overallPercent: totalCount === 0 ? 0 : Math.round((completedCount / totalCount) * 100),
    completedCategories: categories.filter((category) => category.percent === 100).length,
    totalCategories: categories.length,
    totalIncompleteItems: totalCount - completedCount,
    categories,
  };
}

export const CUSTOMERS: CustomerRecord[] = [
  {
    id: 'northbridge-nhs-trust',
    name: 'Northbridge NHS Trust',
    sector: 'Healthcare',
    operatingModel: 'Managed Service',
    owner: 'J. Ellwood',
    sitesCount: 1,
    status: 'Active',
    onboardingStatus: 'Complete',
    commercialReadiness: 'Complete',
    journeyStage: 'Optimising',
    onboardingMilestones: buildMilestoneCategories({}, true),
    riskLevel: 'Low',
    blockers: [],
    missingInfo: [],
    nextBestAction: 'Monitor contract performance ahead of Q3 business review',
    renewalDate: '2027-03-31',
    targetGoLive: '2024-04-01',
    sectionStatuses: {
      customerProfile: { status: 'Complete' },
      commercialModel: { status: 'Complete' },
      contractsTerms: { status: 'Complete' },
      sites: { status: 'Complete' },
      contacts: { status: 'Complete' },
      reportingRequirements: { status: 'Complete' },
      mobilisationPlan: { status: 'Complete' },
    },
    contract: {
      contractRef: 'CTR-2024-014',
      startDate: '2024-04-01',
      endDate: '2027-03-31',
      noticePeriod: '6 months',
      renewalTimer: '20 months',
      status: 'Active',
      slas: ['Menu compliance review: monthly', 'Service incident response: 4 hours'],
      obligations: ['Allergen labelling on all menu items', 'Quarterly nutrition reporting'],
      milestones: [
        { milestone: 'Contract signed', date: '2024-03-15' },
        { milestone: 'Full mobilisation complete', date: '2024-05-20' },
      ],
    },
    commercial: {
      model: 'Managed Service',
      contractValue: '£2.4m / year',
      pricingBasis: 'Management fee + pass-through cost',
      managementFee: '8%',
      rebate: '2.5%',
      billingRules: 'Monthly invoicing in arrears',
      paymentTerms: '30 days',
      margin: '11.2%',
      approvalStatus: 'Approved',
      assumptions: ['420 covers/day average', 'Annual CPI-linked fee uplift'],
    },
    sites: [
      { site: 'Northbridge General Hospital', region: 'Northbridge', serviceModel: 'Managed Service', goLiveDate: '2024-04-01', status: 'Live', headcount: 420, requirements: 'Ward trolley service + retail restaurant' },
    ],
    contacts: [
      {
        name: 'Dr. A. Whitfield', role: 'Facilities Director', category: 'Client', site: 'Northbridge General Hospital', email: 'a.whitfield@northbridge-nhs.example', phone: '01632 960001',
        crm: { provider: 'HubSpot', contactId: '55219873', contactUrl: buildHubSpotContactUrl(HUBSPOT_MOCK_PORTAL_ID, '55219873') },
      },
      { name: 'J. Ellwood', role: 'Account Owner', category: 'Internal Owner', site: 'All sites', email: 'j.ellwood@orderly.example', phone: '01632 900011' },
    ],
    mobilisation: [
      { task: 'Compliance & food safety induction', owner: 'J. Ellwood', dueDate: '2024-04-05', status: 'Complete' },
      { task: 'POS & ordering system rollout', owner: 'R. Okafor', dueDate: '2024-04-15', status: 'Complete' },
    ],
    reporting: [
      { reportType: 'Executive Dashboard', frequency: 'Monthly', format: 'Dashboard', nextDue: '2026-08-01' },
      { reportType: 'Financial Reporting', frequency: 'Monthly', format: 'PDF + CSV', nextDue: '2026-08-05' },
    ],
    activity: [
      { date: '2026-06-30', actor: 'J. Ellwood', action: 'Completed quarterly business review' },
      { date: '2026-04-02', actor: 'System', action: 'Renewal timer updated - 20 months remaining' },
    ],
    crm: {
      provider: 'HubSpot',
      portalId: HUBSPOT_MOCK_PORTAL_ID,
      companyId: '184726391',
      companyUrl: buildHubSpotCompanyUrl(HUBSPOT_MOCK_PORTAL_ID, '184726391'),
      status: 'linked',
      syncEnabled: true,
      lastSyncAt: new Date().toISOString(),
    },
    serviceRequirements: {
      userTypes: ['Patients', 'Ward staff', 'Visitors'],
      serviceTimes: ['Breakfast 7-9am', 'Lunch 12-2pm', 'Dinner 5-7pm', '24/7 ward trolley service'],
      menus: ['Therapeutic diet menu', 'Retail restaurant menu', 'Visitor cafe menu'],
      dietaryRequirements: ['Texture-modified diets', 'Diabetic', 'Renal', 'Vegan', 'Halal'],
      allergens: ['Nuts', 'Gluten', 'Dairy', 'Shellfish'],
      hospitalityNeeds: 'Ward trolley service plus retail restaurant for staff and visitors',
      budget: '£2.4m / year',
      pricingModel: 'Management fee + pass-through cost',
      complianceExpectations: ['HACCP certification required', 'NHS Food Standards compliance', 'Allergen labelling on all menu items'],
      reportingRequirements: ['Monthly nutrition report', 'Quarterly business review pack'],
    },
  },
  {
    id: 'kingsley-academy-trust',
    name: 'Kingsley Academy Trust',
    sector: 'Education',
    operatingModel: 'Cost Plus',
    owner: 'R. Okafor',
    sitesCount: 2,
    status: 'Active',
    onboardingStatus: 'Complete',
    commercialReadiness: 'Complete',
    journeyStage: 'Live',
    onboardingMilestones: buildMilestoneCategories({}, true),
    riskLevel: 'Low',
    blockers: [],
    missingInfo: [],
    nextBestAction: 'Prepare Q3 ESG report ahead of September submission',
    renewalDate: '2026-08-31',
    targetGoLive: '2023-09-01',
    sectionStatuses: {
      customerProfile: { status: 'Complete' },
      commercialModel: { status: 'Complete' },
      contractsTerms: { status: 'Complete' },
      sites: { status: 'Complete' },
      contacts: { status: 'Complete' },
      reportingRequirements: { status: 'Complete' },
      mobilisationPlan: { status: 'Complete' },
    },
    contract: {
      contractRef: 'CTR-2023-031',
      startDate: '2023-09-01',
      endDate: '2026-08-31',
      noticePeriod: '3 months',
      renewalTimer: '13 months',
      status: 'Active',
      slas: ['Menu compliance review: termly', 'Service incident response: next business day'],
      obligations: ['Free school meal reporting', 'Annual ESG submission'],
      milestones: [
        { milestone: 'Contract signed', date: '2023-08-01' },
        { milestone: 'Full mobilisation complete', date: '2023-09-20' },
      ],
    },
    commercial: {
      model: 'Cost Plus',
      contractValue: '£1.1m / year',
      pricingBasis: 'Cost plus management fee',
      managementFee: '6%',
      rebate: '1.8%',
      billingRules: 'Monthly invoicing in arrears',
      paymentTerms: '30 days',
      margin: '7.4%',
      approvalStatus: 'Approved',
      assumptions: ['1,260 pupils across 2 sites', 'Term-time only service'],
    },
    sites: [
      { site: 'Kingsley Secondary School', region: 'Kingsley', serviceModel: 'Cost Plus', goLiveDate: '2023-09-01', status: 'Live', headcount: 950, requirements: 'Cafeteria + free school meal service' },
      { site: 'Elmwood Primary School', region: 'Elmwood', serviceModel: 'Cost Plus', goLiveDate: '2023-09-01', status: 'Live', headcount: 310, requirements: 'Cafeteria service' },
    ],
    contacts: [
      { name: 'M. Osei', role: 'Head of Operations', category: 'Client', site: 'Trust Head Office', email: 'm.osei@kingsleyacademy.example', phone: '01632 960045' },
      { name: 'R. Okafor', role: 'Account Owner', category: 'Internal Owner', site: 'All sites', email: 'r.okafor@orderly.example', phone: '01632 900022' },
    ],
    mobilisation: [
      { task: 'Supplier & menu onboarding', owner: 'R. Okafor', dueDate: '2023-08-25', status: 'Complete' },
      { task: 'Free school meal system setup', owner: 'M. Osei', dueDate: '2023-09-05', status: 'Complete' },
    ],
    reporting: [
      { reportType: 'ESG Reporting', frequency: 'Quarterly', format: 'PDF', nextDue: '2026-09-30' },
      { reportType: 'Compliance Summary', frequency: 'Monthly', format: 'Dashboard', nextDue: '2026-08-01' },
    ],
    activity: [
      { date: '2026-06-12', actor: 'M. Osei', action: 'Confirmed term-time menu changes' },
      { date: '2026-05-01', actor: 'System', action: 'ESG reporting schedule confirmed' },
    ],
    serviceRequirements: {
      userTypes: ['Pupils', 'Teaching staff', 'Visitors'],
      serviceTimes: ['Breakfast club 8-8:45am', 'Lunch service 12-1:30pm'],
      menus: ['Primary school menu', 'Secondary school menu', 'Free school meal menu'],
      dietaryRequirements: ['Vegetarian', 'Vegan', 'Gluten-free', 'Halal'],
      allergens: ['Nuts', 'Sesame', 'Milk', 'Eggs'],
      hospitalityNeeds: 'Occasional parent evening catering and staff hospitality',
      budget: '£1.1m / year',
      pricingModel: 'Cost plus management fee',
      complianceExpectations: ['Free school meal eligibility checks', 'School Food Standards compliance'],
      reportingRequirements: ['Termly ESG report', 'Monthly compliance summary'],
    },
  },
  {
    id: 'meridian-business-park',
    name: 'Meridian Business Park',
    sector: 'Corporate',
    operatingModel: 'Fixed Price',
    owner: 'S. Patel',
    sitesCount: 1,
    status: 'Renewal Due',
    onboardingStatus: 'InProgress',
    commercialReadiness: 'InReview',
    journeyStage: 'Mobilising',
    onboardingMilestones: buildMilestoneCategories({
      commercialSetup: [true, true, true, true, false],
      siteSetup: [true, true, false, false],
      supplierProcurementSetup: [false, false, false, false, false, false, false],
      productMenuReadiness: [false, false, false, false, false],
      operationalReadiness: [false, false, false, false, false, false],
      dataGovernanceChecks: [false, false, false, false],
      launchSignOff: [false, false, false, false],
    }, false),
    riskLevel: 'Medium',
    blockers: ['Signed SLA not yet returned by client'],
    missingInfo: ['Finance contact'],
    nextBestAction: 'Chase signed SLA from client and confirm finance contact',
    renewalDate: '2026-01-12',
    targetGoLive: '2026-08-01',
    sectionStatuses: {
      customerProfile: { status: 'Complete' },
      commercialModel: { status: 'InReview' },
      contractsTerms: { status: 'Partial', detail: 'SLA awaiting client signature' },
      sites: { status: 'Partial' },
      contacts: { status: 'Missing', detail: 'Missing finance contact' },
      reportingRequirements: { status: 'NotStarted' },
      mobilisationPlan: { status: 'InReview' },
    },
    contract: {
      contractRef: 'CTR-2025-002',
      startDate: '2025-01-13',
      endDate: '2026-01-12',
      noticePeriod: '90 days',
      renewalTimer: '48 days',
      status: 'Renewal Due',
      slas: ['Service incident response: 8 hours (pending client sign-off)'],
      obligations: ['Monthly consumption reporting'],
      milestones: [
        { milestone: 'Contract signed', date: '2025-01-05' },
        { milestone: 'Site survey & kitchen sign-off', date: '2026-07-18' },
      ],
    },
    commercial: {
      model: 'Fixed Price',
      contractValue: '£340k / year',
      pricingBasis: 'Fixed price per cover',
      managementFee: 'N/A',
      rebate: '0%',
      billingRules: 'Monthly invoicing in advance',
      paymentTerms: '14 days',
      margin: '6.1%',
      approvalStatus: 'InReview',
      assumptions: ['180 covers/day average', 'Pending confirmation of catering hours'],
    },
    sites: [
      { site: 'Meridian Tower 2', region: 'Meridian', serviceModel: 'Fixed Price', goLiveDate: '2026-08-01', status: 'Mobilising', headcount: 180, requirements: 'Corporate cafe - hours to be confirmed' },
    ],
    contacts: [
      { name: 'C. Nguyen', role: 'Site Manager', category: 'Client', site: 'Meridian Tower 2', email: 'c.nguyen@meridianbp.example', phone: '01632 960112' },
      { name: 'S. Patel', role: 'Account Owner', category: 'Internal Owner', site: 'All sites', email: 's.patel@orderly.example', phone: '01632 900033' },
    ],
    mobilisation: [
      { task: 'Site survey & kitchen sign-off', owner: 'S. Patel', dueDate: '2026-07-18', status: 'In Progress' },
      { task: 'Recruit site catering team', owner: 'S. Patel', dueDate: '2026-07-25', status: 'Not Started' },
      { task: 'Supplier & menu onboarding', owner: 'R. Okafor', dueDate: '2026-08-01', status: 'Not Started', dependency: 'Site survey & kitchen sign-off' },
    ],
    reporting: [],
    activity: [
      { date: '2026-07-05', actor: 'S. Patel', action: 'Chased client for signed SLA' },
      { date: '2026-06-20', actor: 'C. Nguyen', action: 'Requested change to catering hours' },
    ],
    serviceRequirements: {
      userTypes: ['Office staff', 'Visitors'],
      serviceTimes: ['Breakfast 7:30-9:30am', 'Lunch 12-2pm - hours to be confirmed'],
      menus: ['Corporate cafe menu - draft'],
      dietaryRequirements: ['Vegetarian', 'Vegan'],
      allergens: [],
      hospitalityNeeds: 'Ad-hoc meeting room catering, to be confirmed',
      budget: '£340k / year',
      pricingModel: 'Fixed price per cover',
      complianceExpectations: ['Allergen matrix pending client sign-off'],
      reportingRequirements: ['Monthly consumption reporting'],
    },
  },
  {
    id: 'solace-care-homes',
    name: 'Solace Care Homes',
    sector: 'Care',
    operatingModel: 'Management Fee',
    owner: 'J. Ellwood',
    sitesCount: 1,
    status: 'Renewal Due',
    onboardingStatus: 'Complete',
    commercialReadiness: 'Complete',
    journeyStage: 'Live',
    onboardingMilestones: buildMilestoneCategories({}, true),
    riskLevel: 'High',
    blockers: ['Renewal negotiation not yet started - contract expires in under 2 weeks'],
    missingInfo: [],
    nextBestAction: 'Start renewal negotiation immediately',
    renewalDate: '2025-05-31',
    targetGoLive: '2022-06-01',
    sectionStatuses: {
      customerProfile: { status: 'Complete' },
      commercialModel: { status: 'Complete' },
      contractsTerms: { status: 'Complete' },
      sites: { status: 'Complete' },
      contacts: { status: 'Complete' },
      reportingRequirements: { status: 'Complete' },
      mobilisationPlan: { status: 'Complete' },
    },
    contract: {
      contractRef: 'CTR-2022-009',
      startDate: '2022-06-01',
      endDate: '2025-05-31',
      noticePeriod: '3 months',
      renewalTimer: '12 days',
      status: 'Renewal Due',
      slas: ['Menu compliance review: monthly', 'Service incident response: 4 hours'],
      obligations: ['Dietary requirement tracking per resident'],
      milestones: [
        { milestone: 'Contract signed', date: '2022-05-10' },
        { milestone: 'Full mobilisation complete', date: '2022-06-15' },
      ],
    },
    commercial: {
      model: 'Management Fee',
      contractValue: '£680k / year',
      pricingBasis: 'Management fee on cost',
      managementFee: '7.5%',
      rebate: '2.0%',
      billingRules: 'Monthly invoicing in arrears',
      paymentTerms: '45 days',
      margin: '9.8%',
      approvalStatus: 'Approved',
      assumptions: ['65 residents', 'Renewal terms to be re-negotiated'],
    },
    sites: [
      { site: 'Solace Riverside', region: 'Riverside', serviceModel: 'Management Fee', goLiveDate: '2022-06-01', status: 'Live', headcount: 65, requirements: 'Resident dining + special dietary requirements' },
    ],
    contacts: [
      { name: 'H. Bramwell', role: 'Regional Care Manager', category: 'Client', site: 'Solace Riverside', email: 'h.bramwell@solacecare.example', phone: '01632 960078' },
      { name: 'J. Ellwood', role: 'Account Owner', category: 'Internal Owner', site: 'All sites', email: 'j.ellwood@orderly.example', phone: '01632 900011' },
    ],
    mobilisation: [
      { task: 'Dietary requirement audit', owner: 'J. Ellwood', dueDate: '2022-06-10', status: 'Complete' },
      { task: 'POS & ordering system rollout', owner: 'R. Okafor', dueDate: '2022-06-20', status: 'Complete' },
    ],
    reporting: [
      { reportType: 'Compliance Summary', frequency: 'Monthly', format: 'Dashboard', nextDue: '2026-08-01' },
    ],
    activity: [
      { date: '2026-07-01', actor: 'System', action: 'Renewal timer entered high-risk threshold (< 30 days)' },
      { date: '2026-06-15', actor: 'J. Ellwood', action: 'Flagged renewal as high risk - negotiation not started' },
    ],
    crm: {
      provider: 'HubSpot',
      portalId: HUBSPOT_MOCK_PORTAL_ID,
      companyId: '209981204',
      companyUrl: buildHubSpotCompanyUrl(HUBSPOT_MOCK_PORTAL_ID, '209981204'),
      status: 'attention-required',
      syncEnabled: true,
      lastSyncAt: '2026-07-08T11:15:00.000Z',
      lastSyncError: 'HubSpot company record could not be reached during the last sync attempt.',
    },
    serviceRequirements: {
      userTypes: ['Residents', 'Care staff', 'Visiting family'],
      serviceTimes: ['Breakfast 8-9:30am', 'Lunch 12-1:30pm', 'Dinner 5-6:30pm', 'Overnight snacks on request'],
      menus: ['Resident dining menu', 'Texture-modified menu', 'Special occasion menu'],
      dietaryRequirements: ['Texture-modified diets', 'Diabetic', 'Low sodium', 'Vegetarian'],
      allergens: ['Nuts', 'Dairy', 'Gluten'],
      hospitalityNeeds: 'Resident dining plus family visit refreshments',
      budget: '£680k / year',
      pricingModel: 'Management fee on cost',
      complianceExpectations: ['CQC dietary compliance', 'Dietary requirement tracking per resident'],
      reportingRequirements: ['Monthly compliance summary'],
    },
  },
  {
    id: 'harborview-council',
    name: 'Harborview Council',
    sector: 'Local Authority',
    operatingModel: 'Managed Service',
    owner: 'R. Okafor',
    sitesCount: 1,
    status: 'Onboarding',
    onboardingStatus: 'InProgress',
    commercialReadiness: 'InReview',
    journeyStage: 'Onboarding',
    onboardingMilestones: buildMilestoneCategories({
      commercialSetup: [true, true, true, true, false],
      siteSetup: [true, true, true, false],
      supplierProcurementSetup: [true, true, true, false, false, false, false],
      productMenuReadiness: [false, false, false, false, false],
      operationalReadiness: [true, true, true, false, false, false],
      dataGovernanceChecks: [false, false, false, false],
      launchSignOff: [false, false, false, false],
    }, false),
    riskLevel: 'Medium',
    blockers: ['POS rollout dependent on council network access approval'],
    missingInfo: [],
    nextBestAction: 'Finalise POS & ordering system rollout',
    renewalDate: '2029-10-31',
    targetGoLive: '2024-11-01',
    sectionStatuses: {
      customerProfile: { status: 'Complete' },
      commercialModel: { status: 'InReview' },
      contractsTerms: { status: 'Complete' },
      sites: { status: 'Partial' },
      contacts: { status: 'Complete' },
      reportingRequirements: { status: 'NotStarted' },
      mobilisationPlan: { status: 'Blocked', detail: 'POS rollout blocked on network access' },
    },
    contract: {
      contractRef: 'CTR-2024-045',
      startDate: '2024-11-01',
      endDate: '2029-10-31',
      noticePeriod: '12 months',
      renewalTimer: '39 months',
      status: 'Active',
      slas: ['Service incident response: 4 hours'],
      obligations: ['Quarterly reporting cadence to be agreed with client'],
      milestones: [
        { milestone: 'Contract signed', date: '2024-10-10' },
        { milestone: 'POS & ordering system rollout', date: '2026-07-20' },
      ],
    },
    commercial: {
      model: 'Managed Service',
      contractValue: '£910k / year',
      pricingBasis: 'Management fee + pass-through cost',
      managementFee: '9%',
      rebate: '3.0%',
      billingRules: 'Quarterly invoicing in arrears',
      paymentTerms: '60 days',
      margin: '10.4%',
      approvalStatus: 'InReview',
      assumptions: ['240 headcount served', 'Reporting cadence not yet agreed'],
    },
    sites: [
      { site: 'Harborview Civic Centre', region: 'Harborview', serviceModel: 'Managed Service', goLiveDate: '2024-11-01', status: 'Mobilising', headcount: 240, requirements: 'Staff restaurant + public cafe' },
    ],
    contacts: [
      { name: 'T. Iqbal', role: 'Commissioning Lead', category: 'Client', site: 'Harborview Civic Centre', email: 't.iqbal@harborview.gov.example', phone: '01632 960200' },
      { name: 'R. Okafor', role: 'Account Owner', category: 'Internal Owner', site: 'All sites', email: 'r.okafor@orderly.example', phone: '01632 900022' },
    ],
    mobilisation: [
      { task: 'Compliance & food safety induction', owner: 'J. Ellwood', dueDate: '2026-07-12', status: 'Complete' },
      { task: 'POS & ordering system rollout', owner: 'R. Okafor', dueDate: '2026-07-20', status: 'Blocked', dependency: 'Council network access approval' },
      { task: 'Reporting cadence agreed with client', owner: 'J. Ellwood', dueDate: '2026-07-28', status: 'Not Started' },
    ],
    reporting: [],
    activity: [
      { date: '2026-07-08', actor: 'R. Okafor', action: 'Escalated council network access approval delay' },
      { date: '2026-06-25', actor: 'T. Iqbal', action: 'Confirmed civic centre requirements' },
    ],
    serviceRequirements: {
      userTypes: ['Council staff', 'Public visitors'],
      serviceTimes: ['Breakfast 7:30-9am', 'Lunch 12-2pm'],
      menus: ['Staff restaurant menu', 'Public cafe menu'],
      dietaryRequirements: ['Vegetarian', 'Vegan', 'Halal'],
      allergens: ['Nuts', 'Gluten'],
      hospitalityNeeds: 'Staff restaurant plus public-facing cafe',
      budget: '£910k / year',
      pricingModel: 'Management fee + pass-through cost',
      complianceExpectations: ['Public sector food procurement standards'],
      reportingRequirements: ['Reporting cadence to be agreed with client'],
    },
  },
  {
    id: 'fenwick-logistics-group',
    name: 'Fenwick Logistics Group',
    sector: 'Logistics & Distribution',
    operatingModel: 'To be agreed',
    owner: 'S. Patel',
    sitesCount: 0,
    status: 'Onboarding',
    onboardingStatus: 'NotStarted',
    commercialReadiness: 'NotStarted',
    journeyStage: 'Prospect',
    onboardingMilestones: buildMilestoneCategories({
      commercialSetup: [true, false, false, false, false],
    }, false),
    riskLevel: 'Low',
    blockers: [],
    missingInfo: ['Signed contract', 'Site list', 'Finance contact'],
    nextBestAction: 'Progress contract negotiation and confirm site scope',
    renewalDate: '',
    targetGoLive: '2026-11-01',
    sectionStatuses: {
      customerProfile: { status: 'Partial' },
      commercialModel: { status: 'NotStarted' },
      contractsTerms: { status: 'NotStarted' },
      sites: { status: 'NotStarted' },
      contacts: { status: 'Partial' },
      reportingRequirements: { status: 'NotStarted' },
      mobilisationPlan: { status: 'NotStarted' },
    },
    contract: {
      contractRef: '',
      startDate: '',
      endDate: '',
      noticePeriod: '',
      renewalTimer: '',
      status: 'Active',
      slas: [],
      obligations: [],
      milestones: [
        { milestone: 'Initial proposal shared', date: '2026-06-01' },
      ],
    },
    commercial: {
      model: 'To be agreed',
      contractValue: 'TBC',
      pricingBasis: 'TBC',
      managementFee: '',
      rebate: '',
      billingRules: '',
      paymentTerms: '',
      margin: '',
      approvalStatus: 'InReview',
      assumptions: ['Awaiting confirmed site count'],
    },
    sites: [],
    contacts: [
      { name: 'D. Marsh', role: 'Procurement Director', category: 'Client', site: 'Head Office', email: 'd.marsh@fenwicklogistics.example', phone: '01632 960301' },
      { name: 'S. Patel', role: 'Account Owner', category: 'Internal Owner', site: 'All sites', email: 's.patel@orderly.example', phone: '01632 900033' },
    ],
    mobilisation: [],
    reporting: [],
    activity: [
      { date: '2026-06-01', actor: 'S. Patel', action: 'Prospect qualified - proposal shared' },
    ],
    serviceRequirements: {
      userTypes: ['Warehouse staff'],
      serviceTimes: [],
      menus: [],
      dietaryRequirements: [],
      allergens: [],
      hospitalityNeeds: '',
      budget: 'TBC',
      pricingModel: 'TBC',
      complianceExpectations: [],
      reportingRequirements: [],
    },
  },
  {
    id: 'bellcourt-retail-parks',
    name: 'Bellcourt Retail Parks',
    sector: 'Retail',
    operatingModel: 'Fixed Price',
    owner: 'J. Ellwood',
    sitesCount: 3,
    status: 'Onboarding',
    onboardingStatus: 'InProgress',
    commercialReadiness: 'InReview',
    journeyStage: 'ContractWon',
    onboardingMilestones: buildMilestoneCategories({
      commercialSetup: [true, true, true, true, false],
      siteSetup: [false, false, false, false],
    }, false),
    riskLevel: 'Low',
    blockers: [],
    missingInfo: ['Site list confirmation', 'Trading calendars'],
    nextBestAction: 'Create sites and assign site contacts',
    renewalDate: '2029-08-01',
    targetGoLive: '2026-10-01',
    sectionStatuses: {
      customerProfile: { status: 'Complete' },
      commercialModel: { status: 'Complete' },
      contractsTerms: { status: 'Complete' },
      sites: { status: 'NotStarted' },
      contacts: { status: 'Partial' },
      reportingRequirements: { status: 'NotStarted' },
      mobilisationPlan: { status: 'NotStarted' },
    },
    contract: {
      contractRef: 'CTR-2026-051',
      startDate: '2026-08-01',
      endDate: '2029-07-31',
      noticePeriod: '6 months',
      renewalTimer: '36 months',
      status: 'Active',
      slas: ['Service incident response: 8 hours'],
      obligations: ['Quarterly footfall reporting'],
      milestones: [
        { milestone: 'Contract signed', date: '2026-07-01' },
      ],
    },
    commercial: {
      model: 'Fixed Price',
      contractValue: '£560k / year',
      pricingBasis: 'Fixed price per site',
      managementFee: 'N/A',
      rebate: '0%',
      billingRules: 'Monthly invoicing in advance',
      paymentTerms: '30 days',
      margin: '7.0%',
      approvalStatus: 'Approved',
      assumptions: ['3 retail park units'],
    },
    sites: [],
    contacts: [
      { name: 'K. Lindqvist', role: 'Retail Operations Manager', category: 'Client', site: 'Head Office', email: 'k.lindqvist@bellcourtretail.example', phone: '01632 960412' },
      { name: 'J. Ellwood', role: 'Account Owner', category: 'Internal Owner', site: 'All sites', email: 'j.ellwood@orderly.example', phone: '01632 900011' },
    ],
    mobilisation: [
      { task: 'Kick-off meeting', owner: 'J. Ellwood', dueDate: '2026-07-10', status: 'Complete' },
      { task: 'Site list confirmed with client', owner: 'J. Ellwood', dueDate: '2026-07-24', status: 'Not Started' },
    ],
    reporting: [],
    activity: [
      { date: '2026-07-01', actor: 'J. Ellwood', action: 'Contract signed - onboarding kicked off' },
    ],
    crm: {
      provider: 'HubSpot',
      portalId: HUBSPOT_MOCK_PORTAL_ID,
      companyId: '117736450',
      companyUrl: buildHubSpotCompanyUrl(HUBSPOT_MOCK_PORTAL_ID, '117736450'),
      status: 'sync-overdue',
      syncEnabled: true,
      lastSyncAt: '2026-05-02T09:00:00.000Z',
    },
    serviceRequirements: {
      userTypes: ['Retail staff', 'Shoppers'],
      serviceTimes: ['Breakfast 8-10am', 'Lunch 11:30am-2:30pm'],
      menus: ['Food court menu', 'Staff cafe menu'],
      dietaryRequirements: ['Vegetarian', 'Vegan', 'Gluten-free'],
      allergens: ['Nuts', 'Gluten'],
      hospitalityNeeds: 'Food court units across 3 retail parks',
      budget: '£560k / year',
      pricingModel: 'Fixed price per site',
      complianceExpectations: ['Trading calendar alignment across all sites'],
      reportingRequirements: ['Quarterly footfall reporting'],
    },
  },
  {
    id: 'alderney-community-trust',
    name: 'Alderney Community Trust',
    sector: 'Community & Leisure',
    operatingModel: 'Management Fee',
    owner: 'R. Okafor',
    sitesCount: 2,
    status: 'Onboarding',
    onboardingStatus: 'InProgress',
    commercialReadiness: 'Complete',
    journeyStage: 'GoLiveReady',
    onboardingMilestones: buildMilestoneCategories({
      launchSignOff: [true, false, false, false],
    }, true),
    riskLevel: 'Low',
    blockers: [],
    missingInfo: ['Customer owner sign-off'],
    nextBestAction: 'Secure customer sign-off and confirm go-live date',
    renewalDate: '2029-09-01',
    targetGoLive: '2026-08-15',
    sectionStatuses: {
      customerProfile: { status: 'Complete' },
      commercialModel: { status: 'Complete' },
      contractsTerms: { status: 'Complete' },
      sites: { status: 'Complete' },
      contacts: { status: 'Complete' },
      reportingRequirements: { status: 'Complete' },
      mobilisationPlan: { status: 'InReview', detail: 'Awaiting customer owner sign-off' },
    },
    contract: {
      contractRef: 'CTR-2026-038',
      startDate: '2026-09-01',
      endDate: '2029-08-31',
      noticePeriod: '3 months',
      renewalTimer: '38 months',
      status: 'Active',
      slas: ['Service incident response: 4 hours'],
      obligations: ['Monthly community usage reporting'],
      milestones: [
        { milestone: 'Contract signed', date: '2026-05-15' },
        { milestone: 'Site mobilisation complete', date: '2026-07-20' },
      ],
    },
    commercial: {
      model: 'Management Fee',
      contractValue: '£420k / year',
      pricingBasis: 'Management fee on cost',
      managementFee: '7%',
      rebate: '2.0%',
      billingRules: 'Monthly invoicing in arrears',
      paymentTerms: '30 days',
      margin: '8.5%',
      approvalStatus: 'Approved',
      assumptions: ['2 leisure centres', '90 staff headcount'],
    },
    sites: [
      { site: 'Alderney Leisure Centre', region: 'Alderney', serviceModel: 'Management Fee', goLiveDate: '2026-08-15', status: 'Mobilising', headcount: 60, requirements: 'Cafe + vending' },
      { site: 'Alderney Community Hall', region: 'Alderney', serviceModel: 'Management Fee', goLiveDate: '2026-08-15', status: 'Mobilising', headcount: 30, requirements: 'Event catering' },
    ],
    contacts: [
      { name: 'P. Grzelak', role: 'Community Services Manager', category: 'Client', site: 'Alderney Leisure Centre', email: 'p.grzelak@alderneytrust.example', phone: '01632 960455' },
      { name: 'R. Okafor', role: 'Account Owner', category: 'Internal Owner', site: 'All sites', email: 'r.okafor@orderly.example', phone: '01632 900022' },
    ],
    mobilisation: [
      { task: 'Supplier & menu onboarding', owner: 'R. Okafor', dueDate: '2026-07-15', status: 'Complete' },
      { task: 'POS & ordering system rollout', owner: 'R. Okafor', dueDate: '2026-07-25', status: 'Complete' },
      { task: 'Customer owner sign-off', owner: 'P. Grzelak', dueDate: '2026-08-05', status: 'In Progress' },
    ],
    reporting: [
      { reportType: 'Community Usage Report', frequency: 'Monthly', format: 'Dashboard', nextDue: '2026-09-01' },
    ],
    activity: [
      { date: '2026-07-09', actor: 'R. Okafor', action: 'Internal owner signed off go-live readiness' },
      { date: '2026-06-28', actor: 'P. Grzelak', action: 'Confirmed final site requirements' },
    ],
    serviceRequirements: {
      userTypes: ['Leisure centre members', 'Community hall visitors', 'Staff'],
      serviceTimes: ['Cafe hours 8am-6pm', 'Event catering on request'],
      menus: ['Leisure centre cafe menu', 'Vending selection', 'Event catering menu'],
      dietaryRequirements: ['Vegetarian', 'Vegan'],
      allergens: ['Nuts', 'Dairy'],
      hospitalityNeeds: 'Cafe and vending at leisure centre, event catering at community hall',
      budget: '£420k / year',
      pricingModel: 'Management fee on cost',
      complianceExpectations: ['Community usage reporting compliance'],
      reportingRequirements: ['Monthly community usage report'],
    },
  },
];

export function getAllCustomers(): CustomerRecord[] {
  return CUSTOMERS;
}

export function getCustomer(id: string): CustomerRecord | undefined {
  return CUSTOMERS.find((customer) => customer.id === id);
}

/** lazy: in-memory only - replaces the full customer record, used by the Customer Workspace's
 * Save action to commit an edited draft. Upgrade path: replace with a real update-customer API
 * call once the Customer Management backend exists. */
export function saveCustomer(id: string, updated: CustomerRecord): void {
  const index = CUSTOMERS.findIndex((customer) => customer.id === id);
  if (index !== -1) {
    CUSTOMERS[index] = updated;
  }
}

/** Customers still on the onboarding journey (`Prospect`-`GoLiveReady`) - backs the Onboarding
 * Pipeline list. */
export function getOnboardingCustomers(): CustomerRecord[] {
  return CUSTOMERS.filter(isOnboardingCustomer);
}

/** Operational customers (`Live`/`Optimising`) - backs the Customers list. */
export function getLiveCustomers(): CustomerRecord[] {
  return CUSTOMERS.filter((customer) => !isOnboardingCustomer(customer));
}

/** lazy: in-memory only - flips the customer to `Live` once go-live readiness is approved.
 * Upgrade path: replace with a real go-live approval API call once the Customer Management
 * backend exists. */
export function markCustomerLive(id: string): void {
  const customer = CUSTOMERS.find((c) => c.id === id);
  if (customer) {
    customer.journeyStage = 'Live';
    customer.status = 'Active';
    customer.onboardingStatus = 'Complete';
  }
}

/** lazy: in-memory only - toggles a single milestone checklist item for the onboarding detail
 * page. Upgrade path: replace with a real save-progress API call once the Customer Management
 * backend exists. */
export function toggleMilestoneItem(customerId: string, categoryKey: OnboardingMilestoneCategoryKey, itemKey: string): void {
  const customer = CUSTOMERS.find((c) => c.id === customerId);
  const category = customer?.onboardingMilestones.find((c) => c.key === categoryKey);
  const item = category?.items.find((i) => i.key === itemKey);
  if (item) {
    item.complete = !item.complete;
  }
}

/** lazy: in-memory only - explicitly sets (rather than toggles) a milestone item's completion,
 * used when a checklist item's status is derived from real data (e.g. a field being filled in)
 * rather than a manual checkbox. */
export function setMilestoneItemComplete(customerId: string, categoryKey: OnboardingMilestoneCategoryKey, itemKey: string, complete: boolean): void {
  const customer = CUSTOMERS.find((c) => c.id === customerId);
  const category = customer?.onboardingMilestones.find((c) => c.key === categoryKey);
  const item = category?.items.find((i) => i.key === itemKey);
  if (item) {
    item.complete = complete;
  }
}

/** lazy: in-memory only - pushes onto the seeded array so the new customer is browsable for the
 * rest of the session. Upgrade path: replace with a real "create customer" API call once the
 * Customer Management backend exists. */
export function addCustomer(customer: CustomerRecord): void {
  CUSTOMERS.push(customer);
}

export function generateCustomerId(name: string): string {
  const slug = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  let id = slug || 'new-customer';
  let suffix = 1;
  while (CUSTOMERS.some((customer) => customer.id === id)) {
    suffix += 1;
    id = `${slug}-${suffix}`;
  }
  return id;
}

export const RISK_COLOR: Record<RiskLevel, 'success' | 'warning' | 'error'> = {
  Low: 'success',
  Medium: 'warning',
  High: 'error',
};

export const READINESS_COLOR: Record<ReadinessStatus, 'success' | 'warning' | 'error' | 'default'> = {
  Complete: 'success',
  InReview: 'warning',
  Partial: 'warning',
  Missing: 'error',
  NotStarted: 'default',
  Blocked: 'error',
};

export const ONBOARDING_COLOR: Record<OnboardingStatus, 'success' | 'warning' | 'error' | 'default'> = {
  Complete: 'success',
  InProgress: 'warning',
  Blocked: 'error',
  NotStarted: 'default',
};
